import { Bar, CartesianGrid, ComposedChart, Legend, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Card } from "@/components/ui/Card"
import { formatMoney, formatMoneyFull, formatMoneyShort } from "@/lib/money"
import Figure from "./Figure"
import { type DayPoint, PROMO_NAMES, PROVISIONAL_DAYS, TODAY_DAY } from "./landingData"

interface Props {
  currency: string
  plan: number
  planToDate: number
  actual: number
  days: DayPoint[]
  daysLeft: number
  perDayNow: number
  perDayNeeded: number
  perDayPlanned: number
}

const PROVISIONAL_FILL = "color-mix(in srgb, var(--chart-1) 45%, transparent)"

// One plain sentence: where the month ends at the recent pace.
function verdict(p: Props): string {
  const money = (v: number) => formatMoneyShort(v, p.currency)
  if (p.actual >= p.plan) return `October has already reached its plan of ${money(p.plan)}.`
  const endsAt = p.actual + p.perDayNow * p.daysLeft
  const pct = Math.round((endsAt / p.plan) * 100)
  return `At the pace of the last ${PROVISIONAL_DAYS} days, October is projected to end at about ${money(endsAt)} (${pct}% of plan).`
}

// The current month, day by day. Follows Scope and Category; the Period picker does not apply.
export default function MonthPace(props: Props) {
  const { currency, plan, planToDate, actual, days, perDayNow, perDayNeeded } = props
  const behind = actual < planToDate
  const ticks = days.filter((d) => d.day === 1 || d.day % 5 === 0 || d.day === days.length).map((d) => d.label)
  return (
    <Card className="overflow-hidden">
      <div className="border-b px-4 py-3">
        <div className="text-sm font-semibold">October Progress</div>
      </div>
      <div className="grid grid-cols-4 divide-x border-b">
        <Figure label="Month plan" value={formatMoneyShort(plan, currency)} />
        <Figure label="Actual so far" value={formatMoneyShort(actual, currency)} tone={behind ? "warning" : "default"}>
          {`${Math.round((actual / planToDate) * 100)}% of plan up to today`}
        </Figure>
        <Figure label="Selling per day now" value={formatMoneyShort(perDayNow, currency)}>
          Average of the last {PROVISIONAL_DAYS} days
        </Figure>
        <Figure label="Needed per day" value={formatMoneyShort(perDayNeeded, currency)} tone={perDayNeeded > perDayNow ? "warning" : "default"}>
          For the last {props.daysLeft} days
        </Figure>
      </div>
      <div className="border-b px-4 py-3 text-sm">{verdict(props)}</div>
      <div className="px-4 pb-2 pt-4">
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={days} margin={{ top: 16, right: 8, bottom: 0, left: 0 }} barCategoryGap="18%" barGap={1}>
            <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
            <XAxis
              dataKey="label"
              ticks={ticks}
              tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={(v) => formatMoney(Number(v), currency)}
              tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
              axisLine={false}
              tickLine={false}
              width={64}
            />
            <Tooltip
              formatter={(v) => formatMoneyFull(Number(v), currency)}
              labelFormatter={(l) => `${l} Oct${PROMO_NAMES[Number(l)] ? ` · ${PROMO_NAMES[Number(l)]}` : ""}`}
              cursor={{ fill: "var(--muted)" }}
              contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "var(--border)" }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" iconSize={10} />
            <ReferenceLine x={String(TODAY_DAY)} stroke="var(--chart-axis)" strokeDasharray="3 3" label={{ value: "Today", position: "top", fontSize: 11, fill: "var(--chart-axis)" }} />
            <Bar dataKey="plan" name="Plan" fill="var(--chart-muted)" maxBarSize={12} radius={[2, 2, 0, 0]} isAnimationActive={false} />
            <Bar dataKey="actual" name="Actual" stackId="real" fill="var(--chart-1)" maxBarSize={12} radius={[2, 2, 0, 0]} isAnimationActive={false} />
            <Bar dataKey="provisional" name="Actual (provisional)" stackId="real" fill={PROVISIONAL_FILL} maxBarSize={12} radius={[2, 2, 0, 0]} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Card>
  )
}
